<script setup>
import { computed, ref } from 'vue';

/**
 * Categories, states and packages for one window - all three at once.
 *
 * The legacy Revenue Report forces exactly one of these per request, so seeing the same window
 * by category and then by package means running it twice. Here the server returns every
 * dimension in one response and the tabs only switch which one is on screen.
 */
const props = defineProps({
  /** Product-line buckets, each carrying its own package list. */
  productLines: {
    type: Array,
    default: () => [],
  },
  /** Market buckets, each carrying its own package list. */
  markets: {
    type: Array,
    default: () => [],
  },
  /** Flat package leaderboard for the whole window. */
  packages: {
    type: Array,
    default: () => [],
  },
  packageCount: {
    type: Number,
    default: 0,
  },
});

const VIEW = { CATEGORIES: 'categories', STATES: 'states', PACKAGES: 'packages' };

const view = ref(VIEW.CATEGORIES);
const expandedKey = ref(null);

/**
 * The harassment line's two storefronts as expandable groups. Apex site sales have no order, so
 * the server adds them beside the order-based figures rather than inside them.
 * @param {object} bucket Product-line bucket carrying `storefronts`.
 * @returns {object[]}
 */
function storefrontGroups(bucket) {
  const { pfsCheckout, apexSite } = bucket.storefronts;

  return [
    {
      key: 'pfs',
      label: 'PFS checkout',
      total: pfsCheckout.revenue,
      items: pfsCheckout.packages.map(item => ({
        key: `pfs-${item.packageId}`,
        name: item.name,
        units: item.units,
        averagePrice: item.averagePrice,
        amount: item.revenue,
      })),
    },
    {
      key: 'apex',
      label: 'Apex site (legacy checkout)',
      total: apexSite.amount,
      items: apexSite.packages.map((item, index) => ({
        key: `apex-${item.packageId ?? 'none'}-${index}`,
        name: item.name,
        units: item.seats,
        averagePrice: null,
        amount: item.amount,
      })),
    },
  ];
}

const rows = computed(() => {
  switch (view.value) {
    case VIEW.STATES:
      return props.markets.map(bucket => ({
        key: bucket.key,
        label: bucket.label,
        revenue: bucket.revenue,
        units: bucket.units,
        averagePrice: bucket.averagePrice,
        children: bucket.packages,
        groups: null,
        apexAmount: 0,
      }));
    case VIEW.PACKAGES:
      return props.packages.map(row => ({
        key: `pkg-${row.packageId}`,
        label: row.name,
        revenue: row.revenue,
        units: row.units,
        averagePrice: row.averagePrice,
        children: null,
        groups: null,
        apexAmount: 0,
      }));
    default:
      // A line with storefronts is shown at its true total, Apex site included, so it can outrank
      // lines that only count orders.
      return props.productLines
        .map(bucket => (bucket.storefronts
          ? {
            key: bucket.key,
            label: bucket.label,
            revenue: bucket.totalWithApexSite,
            units: bucket.unitsWithApexSite,
            averagePrice: null,
            children: null,
            groups: storefrontGroups(bucket),
            apexAmount: bucket.storefronts.apexSite.amount,
          }
          : {
            key: bucket.key,
            label: bucket.label,
            revenue: bucket.revenue,
            units: bucket.units,
            averagePrice: bucket.averagePrice,
            children: bucket.packages,
            groups: null,
            apexAmount: 0,
          }))
        .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0));
  }
});

/** Apex site sales inside the Categories rows; they have no order, so the other tabs leave them out. */
const apexSiteTotal = computed(() => props.productLines.reduce(
  (sum, bucket) => sum + Number(bucket.storefronts?.apexSite.amount || 0),
  0,
));

const totalRevenue = computed(() => rows.value.reduce((sum, row) => sum + Number(row.revenue || 0), 0));
const peakRevenue = computed(() => Math.max(1, ...rows.value.map(row => Number(row.revenue || 0))));

/**
 * Share of the window's sales, as the report reads it. On Categories the sales include the Apex site.
 * @param {number} revenue Row revenue.
 * @returns {string}
 */
function shareOf(revenue) {
  if (totalRevenue.value <= 0) {
    return '0.0%';
  }
  return `${((revenue / totalRevenue.value) * 100).toFixed(1)}%`;
}

/**
 * Bar width relative to the biggest row, so the smallest row is still visible.
 * @param {number} revenue Row revenue.
 * @returns {string}
 */
function barWidth(revenue) {
  return `${Math.max(1, (Number(revenue || 0) / peakRevenue.value) * 100)}%`;
}

/**
 * Whether a row has anything to open.
 * @param {object} row Row.
 * @returns {boolean}
 */
function hasDetail(row) {
  return Boolean(row.groups) || Boolean(row.children && row.children.length > 0);
}

/**
 * Toggle a bucket open to show the packages inside it.
 * @param {object} row Row.
 */
function toggle(row) {
  if (!hasDetail(row)) {
    return;
  }
  expandedKey.value = expandedKey.value === row.key ? null : row.key;
}

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneyExact = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const number = new Intl.NumberFormat('en-US');
</script>

<template>
  <div :class="$style.card">
    <div :class="$style.tabs">
      <button
        type="button"
        :class="[$style.tab, view === VIEW.CATEGORIES ? $style.tabActive : '']"
        @click="view = VIEW.CATEGORIES; expandedKey = null"
      >
        Categories
      </button>
      <button
        type="button"
        :class="[$style.tab, view === VIEW.STATES ? $style.tabActive : '']"
        @click="view = VIEW.STATES; expandedKey = null"
      >
        States
      </button>
      <button
        type="button"
        :class="[$style.tab, view === VIEW.PACKAGES ? $style.tabActive : '']"
        @click="view = VIEW.PACKAGES; expandedKey = null"
      >
        Packages
      </button>
    </div>

    <p v-if="rows.length === 0" :class="$style.empty">
      Nothing sold in this window.
    </p>

    <p v-if="rows.length > 0 && view === VIEW.CATEGORIES && apexSiteTotal > 0" :class="$style.note">
      Harassment Prevention includes Apex site sales, which have no order record and are counted by
      payment date. Every other category is order revenue, and shares are of the total shown here.
    </p>
    <p v-else-if="rows.length > 0 && view !== VIEW.CATEGORIES && apexSiteTotal > 0" :class="$style.note">
      Order revenue only. Apex site harassment sales ({{ money.format(apexSiteTotal) }}) have no order
      record and are counted on the Categories tab.
    </p>

    <ul v-if="rows.length > 0" :class="$style.rows">
      <li v-for="row in rows" :key="row.key">
        <button
          type="button"
          :class="[$style.row, hasDetail(row) ? $style.rowClickable : '']"
          :aria-expanded="expandedKey === row.key"
          @click="toggle(row)"
        >
          <span :class="$style.rowHead">
            <span :class="$style.rowLabel">
              <span
                v-if="hasDetail(row)"
                :class="[$style.caret, expandedKey === row.key ? $style.caretOpen : '']"
                aria-hidden="true"
              >›</span>
              {{ row.label }}
            </span>
            <span :class="$style.rowFigure">
              <span :class="$style.rowValue">{{ moneyExact.format(row.revenue) }}</span>
              <span v-if="row.apexAmount > 0" :class="$style.rowHint">
                incl. Apex site {{ money.format(row.apexAmount) }}
              </span>
            </span>
          </span>
          <span :class="$style.track">
            <span :class="$style.bar" :style="{ width: barWidth(row.revenue) }" />
          </span>
          <span :class="$style.rowFoot">
            <span>{{ number.format(row.units) }} registrations</span>
            <!-- What a registration actually went for, after every discount. The same revenue
                 can be more registrations sold cheaper, which revenue alone hides. -->
            <span v-if="row.averagePrice">{{ moneyExact.format(row.averagePrice) }} each</span>
            <span>{{ shareOf(row.revenue) }} of sales</span>
          </span>
        </button>

        <div v-if="expandedKey === row.key && row.groups" :class="$style.groups">
          <section
            v-for="group in row.groups"
            :key="group.key"
            :class="$style.group"
          >
            <h4 :class="$style.groupHead">
              <span>{{ group.label }}</span>
              <span :class="$style.groupTotal">{{ money.format(group.total) }}</span>
            </h4>
            <ul v-if="group.items.length" :class="$style.groupItems">
              <li
                v-for="item in group.items"
                :key="item.key"
                :class="$style.child"
              >
                <span :class="$style.childName">{{ item.name }}</span>
                <span :class="$style.childMeta">
                  {{ number.format(item.units) }} reg
                  <template v-if="item.averagePrice"> · {{ moneyExact.format(item.averagePrice) }} each</template>
                </span>
                <span :class="$style.childValue">{{ money.format(item.amount) }}</span>
              </li>
            </ul>
            <p v-else :class="$style.groupEmpty">None in this window.</p>
          </section>
        </div>

        <ul v-else-if="expandedKey === row.key" :class="$style.children">
          <li
            v-for="child in row.children"
            :key="child.packageId"
            :class="$style.child"
          >
            <span :class="$style.childName">{{ child.name }}</span>
            <span :class="$style.childMeta">
              {{ number.format(child.units) }} reg
              <template v-if="child.averagePrice"> · {{ moneyExact.format(child.averagePrice) }} each</template>
            </span>
            <span :class="$style.childValue">{{ money.format(child.revenue) }}</span>
          </li>
        </ul>
      </li>
    </ul>

    <p v-if="view === VIEW.PACKAGES && packageCount > packages.length" :class="$style.more">
      Showing the top {{ packages.length }} of {{ number.format(packageCount) }} packages sold.
    </p>
  </div>
</template>

<style module>
.card {
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 1px 2px var(--shadow-color);
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.tabs {
  display: flex;
  gap: 4px;
  background: var(--background-color);
  border-radius: var(--border-radius-xxl);
  padding: 4px;
}
.tab {
  flex: 1;
  border: none;
  background: transparent;
  border-radius: var(--border-radius-xxl);
  padding: 8px 12px;
  font-size: var(--font-size-lg);
  font-weight: 500;
  color: var(--primary-font-color);
  cursor: pointer;
}
.tabActive {
  background: white;
  color: var(--primary-font-dark-color);
  box-shadow: 0 1px 2px var(--shadow-color);
}
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  font: inherit;
  cursor: default;
}
.rowClickable {
  cursor: pointer;
}
.rowHead {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
}
.rowLabel {
  font-size: var(--font-size-xl);
  color: var(--primary-font-dark-color);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.caret {
  display: inline-block;
  color: var(--faint-color);
  transition: transform 0.15s ease;
}
.caretOpen {
  transform: rotate(90deg);
}
.rowValue {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--primary-font-dark-color);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.track {
  display: block;
  height: 6px;
  border-radius: 3px;
  background: var(--background-color);
  overflow: hidden;
}
.bar {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--pfs-teal);
  opacity: 0.55;
}
.rowFoot {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: var(--font-size-md);
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
}
.rowFigure {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
}
.rowHint {
  font-size: var(--font-size-md);
  color: var(--faint-color);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.groups {
  margin: 10px 0 0;
  padding: 10px 0 0 14px;
  border-top: 1px solid var(--border-color-light);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.groupHead {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: baseline;
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--faint-color);
}
.groupTotal {
  font-variant-numeric: tabular-nums;
}
.groupItems {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.groupEmpty {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.note {
  margin: 0;
  color: var(--faint-color);
  font-size: var(--font-size-md);
}
.children {
  list-style: none;
  margin: 10px 0 0;
  padding: 10px 0 0 14px;
  border-top: 1px solid var(--border-color-light);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.child {
  display: flex;
  gap: 12px;
  align-items: baseline;
  font-size: var(--font-size-lg);
}
.childName {
  flex: 1;
  color: var(--primary-font-color);
}
.childMeta {
  color: var(--faint-color);
  font-size: var(--font-size-md);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.childValue {
  color: var(--primary-font-dark-color);
  font-weight: 500;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.empty, .more {
  margin: 0;
  color: var(--faint-color);
  font-size: var(--font-size-md);
}
.empty {
  padding: 20px 0;
  text-align: center;
  font-size: var(--font-size-lg);
}
</style>
